/**
 * remoteConfig.ts
 *
 * The injection seam through which the app reads its remote configuration:
 * the maintenance window.
 *
 * Like `clients.ts`, this module is free of Pinia, router and vendor SDK
 * imports. The store imports it, so anything added here would be pulled
 * into the store graph; the concrete Firebase implementation lives in
 * `appRemoteConfig.ts` and is registered once at startup.
 */
import { type App, type InjectionKey, inject } from 'vue'
import {
  INACTIVE_MAINTENANCE_WINDOW,
  type MaintenanceWindow,
} from '@/utils/maintenance'

/** A source of maintenance configuration. */
export interface RemoteConfigSource {
  /**
   * Fetches the current maintenance window.
   *
   * @param force - Bypasses the cache. Used by the retry button, which a
   *   user presses precisely because they want an answer newer than the
   *   polling interval would give them.
   */
  fetchMaintenance(force?: boolean): Promise<MaintenanceWindow>
}

/**
 * A source that always reports no maintenance.
 *
 * This is the fallback when no source has been registered. It fails open: a
 * missing Firebase configuration leaves the app usable rather than stranding
 * every user on a maintenance page.
 */
export const inactiveRemoteConfig: RemoteConfigSource = {
  fetchMaintenance: async () => ({ ...INACTIVE_MAINTENANCE_WINDOW }),
}

/** Injection key for the remote configuration source. */
export const remoteConfigKey: InjectionKey<RemoteConfigSource> = Symbol('remoteConfig')

let defaultSource: RemoteConfigSource | null = null

/**
 * Registers the source used when no provider is present.
 *
 * Called once at startup by `appRemoteConfig.ts`. Tests pass null to reset
 * it between cases.
 *
 * @param source - The source to fall back to, or null to clear it.
 */
export function setDefaultRemoteConfig(source: RemoteConfigSource | null): void {
  defaultSource = source
}

/**
 * Makes the source available to every component and store in the app.
 *
 * @param app - The Vue application instance.
 * @param source - The source to provide.
 */
export function provideRemoteConfig(app: App, source: RemoteConfigSource): void {
  app.provide(remoteConfigKey, source)
}

/**
 * Returns the remote configuration source.
 *
 * Resolves the provided source first, then the one registered at startup,
 * and finally the inactive fallback.
 *
 * @returns The source to read maintenance configuration from.
 */
export function useRemoteConfig(): RemoteConfigSource {
  return inject(remoteConfigKey, null) ?? defaultSource ?? inactiveRemoteConfig
}
